// Shift briefing (#13): pick Lesson 1 or a Standard shift, which unlocked
// aircraft fly a Standard shift, and the settings (speed of play, season
// length). Shows your level and the season day. Shown before the first shift
// and after each one ends.

import { PACES } from '../config/constants.js';
import {
  hangarFor, shiftUnlocked, aircraftForShift, levelLabel, seasonDone,
} from '../core/career.js';
import { $, setText } from './dom.js';

export const paceLabel = (value) => {
  const p = PACES.find((x) => x.value === value);
  return p ? `${p.label} (${p.value}×)` : `${value}×`;
};

/** A speed and what it does to points, e.g. "Fast (1.5×) · points ×1.2". */
export const paceText = (value) => {
  const bonus = PACES.find((x) => x.value === value)?.bonus ?? 1;
  return `${paceLabel(value)} · points ×${bonus}`;
};

/** The next speed up (step 1) or down (step -1), or null at either end. */
export function paceStep(value, step) {
  const i = PACES.findIndex((p) => p.value === value);
  const next = PACES[(i < 0 ? 1 : i) + step];
  return next ? next.value : null;
}

export function createBriefing(veil, { onStart, onNewSeason, onSettings }) {
  const list = $('#hangar-list', veil);
  const chosen = new Set(); // optional (unlocked, not required) hangar slots the player ticked
  let career = null;
  let settings = null;

  const paceRange = $('#pace-range', veil);
  const seasonRange = $('#season-range', veil);
  paceRange.max = String(PACES.length - 1);

  function renderSettings() {
    paceRange.value = String(Math.max(0, PACES.findIndex((p) => p.value === settings.pace)));
    setText($('#pace-value', veil), paceText(settings.pace));
    seasonRange.value = String(settings.seasonDays);
    setText($('#season-value', veil), `${settings.seasonDays} ${settings.seasonDays === 1 ? 'day' : 'days'}`);
  }

  function renderSeason() {
    const done = seasonDone(career, settings.seasonDays);
    setText($('#career-level', veil), levelLabel(career.level));
    setText($('#standard-level', veil), levelLabel(career.level));
    setText($('#career-season', veil), done
      ? `Season complete (${settings.seasonDays} days)`
      : `Season day ${career.day} of ${settings.seasonDays}`);
    $('#season-done', veil).hidden = !done;
    const standard = $('.lesson[data-shift="standard"]', veil);
    const open = shiftUnlocked(career, 'standard');
    standard.disabled = !open || done;
    $('#standard-lock', veil).hidden = open;
  }

  paceRange.addEventListener('input', () => {
    settings = { ...settings, pace: PACES[Number(paceRange.value)].value };
    renderSettings();
    onSettings(settings);
  });
  seasonRange.addEventListener('input', () => {
    settings = { ...settings, seasonDays: Number(seasonRange.value) };
    renderSettings();
    renderSeason();
    onSettings(settings);
  });
  $('#new-season', veil).addEventListener('click', () => {
    career = onNewSeason();
    renderSeason();
    $('.lesson[data-shift="standard"]', veil).focus();
  });

  function renderHangar() {
    list.innerHTML = '';
    for (const h of hangarFor(career)) {
      const row = document.createElement('label');
      row.className = `hangar-row${h.unlocked ? '' : ' locked'}`;
      if (h.later) {
        row.innerHTML = `<input type="checkbox" disabled><span class="h-name">${h.label}</span><span class="h-note">Coming later</span>`;
      } else {
        const checked = h.required || chosen.has(h.id);
        const note = h.required ? 'Always flies' : (h.unlocked ? 'Unlocked' : `${h.needs} more career points`);
        row.innerHTML = `<input type="checkbox" ${checked ? 'checked' : ''} ${h.required || !h.unlocked ? 'disabled' : ''}>`
          + `<span class="h-name">${h.label}</span><span class="h-type">${h.typeLabel}</span><span class="h-note">${note}</span>`;
        const box = $('input', row);
        box.addEventListener('change', () => { if (box.checked) chosen.add(h.id); else chosen.delete(h.id); });
      }
      list.appendChild(row);
    }
  }

  veil.addEventListener('click', (e) => {
    const card = e.target.closest('.lesson');
    if (!card || card.disabled) return;
    const kind = card.dataset.shift;
    // Lesson 1 and free play start with a scout and a duster (free play can add more)
    const aircraft = kind === 'standard' ? aircraftForShift(career, [...chosen]) : ['scout', 'duster'];
    veil.hidden = true;
    onStart({ kind, aircraft });
  });

  return {
    open(nextCareer, nextSettings) {
      career = nextCareer;
      settings = nextSettings;
      setText($('#career-points', veil), career.points);
      const standard = $('.lesson[data-shift="standard"]', veil);
      renderSettings();
      renderSeason();
      // after Lesson 1, suggest the Standard shift first
      veil.querySelector('.lesson[data-shift="orientation"]').classList.toggle('suggested', !career.orientationDone);
      standard.classList.toggle('suggested', career.orientationDone);
      renderHangar();
      veil.hidden = false;
      if (seasonDone(career, settings.seasonDays)) $('#new-season', veil).focus();
      else (career.orientationDone ? standard : $('.lesson[data-shift="orientation"]', veil)).focus();
    },
    isOpen: () => !veil.hidden,
  };
}
